import type { ReactNode } from 'react';
import { cx } from '../primitives/cx.js';
import { Num } from '../primitives/Num.tsx';

/**
 * The hero plate, and the figures on it.
 *
 * A dark panel carrying the one or two figures a page is actually about, set
 * large in the serif. Everything below it is quiet by comparison, which is the
 * point: somebody arriving at a screen should be able to read the number they
 * came for before deciding whether to read anything else.
 *
 * Every figure states its scope underneath. A total whose scope is invisible
 * is not a smaller number than it looks - it is a number the reader has no way
 * to size, and that is worse than no number.
 */

export function Hero({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & Record<string, unknown>): JSX.Element {
  return (
    <section
      {...rest}
      data-component="Hero"
      // The dark palette, scoped. A plate is an island in a light page rather
      // than a theme, so the tokens are switched for this subtree and every
      // colour inside it resolves without a single dark-mode utility.
      data-theme="dark"
      className={cx(
        'flex flex-wrap items-end gap-6 rounded-card bg-plate px-6 py-5 text-ink',
        className,
      )}
    >
      {children}
    </section>
  );
}

/**
 * One figure.
 *
 * The currency is set smaller and before the number rather than inside it, so
 * a column of figures aligns on the digits. `scope` is not decoration: it is
 * where a multi-currency total admits what it leaves out.
 */
export function Figure({
  label,
  value,
  unit,
  scope,
  size = 'large',
  className,
  ...rest
}: {
  label: ReactNode;
  value: ReactNode;
  /** A currency code, set smaller beside the figure. */
  unit?: ReactNode;
  /** What the figure covers, and what it does not. */
  scope?: ReactNode;
  size?: 'large' | 'secondary';
  className?: string;
} & Record<string, unknown>): JSX.Element {
  return (
    <div {...rest} data-component="Figure" className={cx('min-w-0', className)}>
      <p className="pb-2.5 text-micro uppercase tracking-kicker text-bronze-text">{label}</p>
      <p className={cx('font-serif leading-heading', size === 'large' ? 'text-display' : 'text-h2')}>
        {unit === undefined ? null : (
          <span className="pe-1 text-lead text-ink-600">{unit}</span>
        )}
        <Num>{value}</Num>
      </p>
      {scope === undefined ? null : (
        <p className="pt-2 text-dense text-ink-600">{scope}</p>
      )}
    </div>
  );
}

/**
 * A quantity against the quantity it is allowed to reach.
 *
 * Both ends are written underneath, because a bar on its own says "quite far
 * along" and nothing else.
 */
export function ProgressTrack({
  value,
  max,
  startLabel,
  endLabel,
  className,
  ...rest
}: {
  value: number;
  max: number;
  startLabel?: ReactNode;
  endLabel?: ReactNode;
  className?: string;
} & Record<string, unknown>): JSX.Element {
  const pct = max <= 0 ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div {...rest} data-component="ProgressTrack" className={cx('min-w-0 flex-1', className)}>
      <div
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-1 overflow-hidden rounded-rail bg-sunken"
      >
        <i
          data-plate-fill
          style={{ inlineSize: `${String(pct)}%` }}
          className="block h-full rounded-rail bg-bronze transition-[inline-size] duration-overlay ease-out motion-reduce:transition-none"
        />
      </div>
      {startLabel === undefined && endLabel === undefined ? null : (
        <div className="flex justify-between pt-1 text-micro text-ink-meta">
          <Num>{startLabel}</Num>
          <Num>{endLabel}</Num>
        </div>
      )}
    </div>
  );
}

/**
 * Who is on this team, as monograms.
 *
 * Overlapped, and the accessible name is the list of names - three circles
 * reading "LH OK PR" to a screen reader is three pieces of nothing.
 */
export function AvatarStack({
  people,
  className,
  ...rest
}: {
  people: Array<{ id: string; name: string }>;
  className?: string;
} & Record<string, unknown>): JSX.Element {
  const initials = (name: string): string =>
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0] ?? '')
      .join('')
      .toUpperCase();
  return (
    <div
      {...rest}
      data-component="AvatarStack"
      className={cx('flex', className)}
      aria-label={people.map((person) => person.name).join(', ')}
    >
      {people.map((person) => (
        <span
          key={person.id}
          data-avatar={person.id}
          title={person.name}
          className={cx(
            'flex h-avatar w-avatar items-center justify-center rounded-chip border border-rule',
            'bg-bronze-tint text-meta font-medium text-bronze-text -ms-2.5 first:ms-0',
          )}
        >
          {initials(person.name)}
        </span>
      ))}
    </div>
  );
}
